import { useId } from 'react';
import unescapedText from 'I18n/utils/unescapedText';
import styled, { css } from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const BreezeLoaderMotion = {
  WAITING: 'WAITING',
  WORKING: 'WORKING'
};
const SPARKLE_PATH = 'M14.36236,13.828641c-7.17984,0-13.17528-5.882486-13.17528-12.93287C1.18736,0.408103,0.7718,0,0.275,0c-.50322,0-.91236.401714-.91236.895765c0,7.05045-5.99532,12.932876-13.17525,12.932876-.504439,0-.91239.401716-.91239.895729v.0012c0,.495273.407951.895789.91239.895789c7.17981,0,13.17525,5.882486,13.17525,12.932852c0,.487652.41556.895789.91236.895789.50322,0,.91236-.401716.91236-.895789c0-7.050426,5.99526-12.932852,13.17528-12.932852.50442,0,.91236-.401716.91236-.895789v-.0012c0-.495213-.40794-.895729-.91236-.895729Z';
const SPARKLE_VIEWBOX = '-15 -1 31 31';
const spinAnimation = css(["animation:breeze-loader-spin 1.333s linear infinite;@media (prefers-reduced-motion:reduce){animation:none;}@keyframes breeze-loader-spin{0%{transform:rotate(0deg);animation-timing-function:cubic-bezier(0.333,0,0,1);}50%{transform:rotate(180deg);}100%{transform:rotate(180deg);}}"]);
const workingAnimation = css(["transform-origin:center;animation:breeze-loader-working-rotate 2.5s linear infinite,breeze-loader-working-scale 2.5s linear infinite,breeze-loader-working-color 2.5s linear infinite,breeze-loader-working-glow 2.5s linear infinite;@media (prefers-reduced-motion:reduce){animation:none;filter:none;color:var(--t-sys-color-expressive-ai-fg-active-stop1-color,#ff4800);}@keyframes breeze-loader-working-rotate{0%{rotate:0deg;}12.5%{rotate:0deg;animation-timing-function:cubic-bezier(0.5,0,0.5,0.4);}50%{rotate:200deg;animation-timing-function:cubic-bezier(0.3,0.45,0.5,1);}87.5%{rotate:360deg;}100%{rotate:360deg;}}@keyframes breeze-loader-working-scale{0%{scale:1;}12.5%{scale:1;animation-timing-function:cubic-bezier(0.35,0,0.7,1);}25%{scale:0.45;animation-timing-function:cubic-bezier(0.3,1.25,0.6,1);}37.5%{scale:1;animation-timing-function:linear;}62.5%{scale:1;animation-timing-function:cubic-bezier(0.35,0,0.7,1);}75%{scale:0.45;animation-timing-function:cubic-bezier(0.3,1.25,0.6,1);}87.5%{scale:1;}100%{scale:1;}}@keyframes breeze-loader-working-color{0%,12.5%,87.5%,100%{color:var(--t-sys-color-expressive-ai-fg-active-stop1-color,#ff4800);}50%{color:var(--t-sys-color-expressive-ai-fg-active-stop2-color-2,#016de1);}}@keyframes breeze-loader-working-glow{0%,12.5%,87.5%,100%{filter:drop-shadow( 0 0 12px color-mix( in srgb,var(--t-sys-color-expressive-ai-fg-active-stop1-color,#ff4800) 80%,transparent ) );}50%{filter:drop-shadow( 0 0 12px color-mix( in srgb,var(--t-sys-color-expressive-ai-fg-active-stop2-color-2,#016de1) 80%,transparent ) );}}"]);
const gradientStyles = css([".breeze-loader-stop-start{animation:breeze-loader-gradient 8s ease-in-out infinite;}.breeze-loader-stop-end{animation:breeze-loader-gradient 8s ease-in-out -1s infinite;}@media (prefers-reduced-motion:reduce){.breeze-loader-stop-start,.breeze-loader-stop-end{animation:none;}}@keyframes breeze-loader-gradient{0%,100%{stop-color:var(--breeze-loader-color-orange);}37%{stop-color:var(--breeze-loader-color-pink);}50%{stop-color:var(--breeze-loader-color-blue);}63%{stop-color:var(--breeze-loader-color-pink);}}"]);
const StyledBreezeLoaderContainer = styled.div.withConfig({
  displayName: "BreezeLoader__StyledBreezeLoaderContainer"
})(["--breeze-loader-color-orange:#ff4800;--breeze-loader-color-blue:#016de1;--breeze-loader-color-pink:#ff9fcc;position:relative;display:inline-flex;align-items:center;justify-content:center;"]);
const StyledBreezeLoader = styled.svg.withConfig({
  displayName: "BreezeLoader__StyledBreezeLoader"
})(["", " ", ""], ({
  $motion
}) => $motion === BreezeLoaderMotion.WAITING ? spinAnimation : workingAnimation, ({
  $motion
}) => $motion === BreezeLoaderMotion.WAITING && gradientStyles);
const SparkleGradient = ({
  gradientId
}) => /*#__PURE__*/_jsx("defs", {
  children: /*#__PURE__*/_jsxs("linearGradient", {
    id: gradientId,
    x1: "0%",
    y1: "0%",
    x2: "100%",
    y2: "0%",
    children: [/*#__PURE__*/_jsx("stop", {
      offset: "0%",
      className: "breeze-loader-stop-start"
    }), /*#__PURE__*/_jsx("stop", {
      offset: "100%",
      className: "breeze-loader-stop-end"
    })]
  })
});
export const BreezeLoader = ({
  size = 24,
  motion = BreezeLoaderMotion.WORKING,
  'data-test-id': dataTestId
}) => {
  const uid = useId();
  const mainGradientId = `${uid}-gradient`;
  return /*#__PURE__*/_jsx(StyledBreezeLoaderContainer, {
    role: "status",
    "aria-label": unescapedText('aiComponentsUiLibrary.components.breezeLoader.ariaLabel'),
    "data-test-id": dataTestId || 'breeze-loader',
    children: /*#__PURE__*/_jsxs(StyledBreezeLoader, {
      $motion: motion,
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: SPARKLE_VIEWBOX,
      height: size,
      width: size,
      children: [motion === BreezeLoaderMotion.WAITING && /*#__PURE__*/_jsx(SparkleGradient, {
        gradientId: mainGradientId
      }), /*#__PURE__*/_jsx("path", {
        d: SPARKLE_PATH,
        fill: motion === BreezeLoaderMotion.WORKING ? 'currentColor' : `url(#${mainGradientId})`
      })]
    })
  });
};
BreezeLoader.displayName = 'BreezeLoader';